/**
 * Единственное место, где задаются табличные данные для клиентов распределительного коллектора.
 *
 * Ключ объекта — шаг мощности (кВт), DISTRIBUTION_COLLECTOR_CLIENT_POWER_STEPS (50 … 1000).
 * Ввод 40 кВт подбирается на строку 50; отдельного пресета 40 кВт нет.
 *
 * flowRate — м³/ч при referenceDeltaTemp для номинала кВт (ключ объекта): P·c·ΔT, вода c=4190, 0,01 м³/ч.
 * Геометрию (diameter, connectionSize, …) править здесь; логика — в CollectorCharacteristicsCalculator.
 *
 * connectionType: 'nipple' | 'coupling' | 'flange' | 'nut' | 'washer'
 * connectionSize: как в каталоге CONNECTION_SIZE_CATALOG (endConnectionVelocitySizing.js).
 *
 * Давление (бар) и заглушка у круга связаны: в UI «6» → прямая при ⌀≤60.3 и наличии прямой
 * заглушки в каталоге; «8» → эллиптическая (8/10). У квадрата — 4 или 6 (4/6 бар в подписи).
 * Можно задать pressureRound / pressureSquare; либо одно pressure для обоих сечений;
 * либо только capType для круга: 'straight' | 'elliptical' (6 и 8 бар соответственно, если
 * нет явного pressureRound).
 */

import { COLLECTOR_END_CONNECTION_CATEGORIES } from './endConnectionVelocitySizing';

export function connectionTypeToCollectorCategoryLabel(connectionType) {
  const row = COLLECTOR_END_CONNECTION_CATEGORIES.find((c) => c.connectionType === connectionType);
  return row?.id ?? 'Ниппели';
}

/** @typedef {{ connectionType: string, connectionSize: number }} DistributionCollectorClientConnectionVariant */

/**
 * @typedef {Object} DistributionCollectorClientRow
 * @property {number} flowRate — м³/ч (номинальный расход пресета, вода)
 * @property {number} [referenceDeltaTemp] — эталонная ΔT (°C), при которой подобран flowRate
 * @property {number} diameter — наружный ⌀ гребёнки, круг
 * @property {string} squareProfile — профиль квадрата (например '80x80')
 * @property {number} [pressure] — бар, если не заданы отдельно для сечений
 * @property {number} [pressureRound] — бар для круглого сечения
 * @property {number} [pressureSquare] — бар для квадратного
 * @property {'straight' | 'elliptical'} [capType] — тип заглушки круга (если нет pressureRound)
 * @property {string} [connectionType]
 * @property {number} [connectionSize] — ключ размера в каталоге торцевого подключения
 * @property {DistributionCollectorClientConnectionVariant} [nipple]
 * @property {DistributionCollectorClientConnectionVariant} [flange]
 */

/**
 * Давление из пресета с учётом сечения. После применения сработают обычные правила калькулятора
 * (например принудительно 8 бар при круглом ⌀ > 60.3).
 * @param {DistributionCollectorClientRow} raw
 */
/** Шаги номинала для распределительного коллектора и комбинированного узла (гребёнка). */
export const DISTRIBUTION_COLLECTOR_CLIENT_POWER_STEPS = [
  50, 60, 85, 135, 170, 200, 250, 400, 600, 800, 1000,
];

/** Пресет 60 кВт: 60×60 допускается ниже 0,2 м/с (стандартное изделие). */
export function isDistributionClientPresetSquareVelocityException(stepKw, profileValue) {
  return stepKw === 60 && profileValue === '60x60';
}

export function resolvePresetPressure(raw, isSquare) {
  if (isSquare) {
    if (raw.pressureSquare != null) return Number(raw.pressureSquare);
    if (raw.pressure != null) return Number(raw.pressure);
    return 4;
  }
  if (raw.pressureRound != null) return Number(raw.pressureRound);
  if (raw.pressure != null) return Number(raw.pressure);
  if (raw.capType === 'elliptical') return 8;
  if (raw.capType === 'straight') return 6;
  return 6;
}

/** @type {Object<number, DistributionCollectorClientRow>} */
export const DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE = {
  50: {
    diameter: 48.3,
    squareProfile: '50x50',
    capType: 'straight',
    pressureRound: 6,
    pressureSquare: 4,
    connectionType: 'nipple',
    connectionSize: 26.9,
    flowRate: 1.72,
    referenceDeltaTemp: 25,
  },
  60: {
    diameter: 60.3,
    squareProfile: '60x60',
    capType: 'straight',
    pressureRound: 6,
    pressureSquare: 4,
    connectionType: 'nipple',
    connectionSize: 33.7,
    flowRate: 2.06,
    referenceDeltaTemp: 25,
  },
  85: {
    diameter: 60.3,
    squareProfile: '60x60',
    capType: 'straight',
    pressureRound: 6,
    pressureSquare: 4,
    connectionType: 'nipple',
    connectionSize: 42.4,
    flowRate: 2.92,
    referenceDeltaTemp: 25,
  },
  135: {
    diameter: 60.3,
    squareProfile: '60x60',
    capType: 'straight',
    pressureRound: 6,
    pressureSquare: 4,
    connectionType: 'nipple',
    connectionSize: 48.3,
    flowRate: 4.64,
    referenceDeltaTemp: 25,
  },
  170: {
    diameter: 76.1,
    squareProfile: '80x80',
    capType: 'elliptical',
    pressureRound: 8,
    pressureSquare: 4,
    connectionType: 'nipple',
    connectionSize: 48.3,
    flowRate: 5.84,
    referenceDeltaTemp: 25,
  },
  200: {
    diameter: 76.1,
    squareProfile: '80x80',
    capType: 'elliptical',
    pressureRound: 8,
    pressureSquare: 4,
    connectionType: 'nipple',
    connectionSize: 48.3,
    flowRate: 6.87,
    referenceDeltaTemp: 25,
  },
  250: {
    diameter: 76.1,
    squareProfile: '80x80',
    capType: 'elliptical',
    pressureRound: 8,
    pressureSquare: 4,
    flowRate: 10.74,
    referenceDeltaTemp: 20,
    nipple: { connectionType: 'nipple', connectionSize: 60.3 },
    flange: { connectionType: 'flange', connectionSize: 50 },
  },
  400: {
    diameter: 114.3,
    squareProfile: '100x100',
    capType: 'elliptical',
    pressureRound: 8,
    pressureSquare: 4,
    connectionType: 'flange',
    connectionSize: 65,
    flowRate: 17.18,
    referenceDeltaTemp: 20,
  },
  600: {
    diameter: 114.3,
    squareProfile: '100x100',
    capType: 'elliptical',
    pressureRound: 8,
    pressureSquare: 4,
    connectionType: 'flange',
    connectionSize: 80,
    flowRate: 25.78,
    referenceDeltaTemp: 20,
  },
  800: {
    diameter: 139.7,
    squareProfile: '150x150',
    capType: 'elliptical',
    pressureRound: 8,
    pressureSquare: 4,
    connectionType: 'flange',
    connectionSize: 100,
    flowRate: 34.37,
    referenceDeltaTemp: 20,
  },
  1000: {
    diameter: 159,
    squareProfile: '150x150',
    capType: 'elliptical',
    pressureRound: 8,
    pressureSquare: 4,
    connectionType: 'flange',
    connectionSize: 100,
    flowRate: 42.96,
    referenceDeltaTemp: 20,
  },
};
